import {
	createContext,
	type ReactNode,
	type SetStateAction,
	useCallback,
	useContext,
	useMemo,
	useState,
} from "react";
import type { EntryFilterState } from "@/features/attorney/views/Entries/filters/types";

export interface EntryViewDateRange {
	from: Date | undefined;
	to: Date | undefined;
}

export type EntryFilterOverrides = Partial<EntryFilterState>;

interface EntryViewStateContextValue {
	date: Date;
	dateRange: EntryViewDateRange;
	viewedMonth: Date;
	filterOverrides: EntryFilterOverrides;
	setDate: (date: Date) => void;
	setDateRange: (range: EntryViewDateRange) => void;
	setViewedMonth: (month: Date) => void;
	setFilterOverrides: (value: SetStateAction<EntryFilterOverrides>) => void;
	resetFilters: () => void;
}

const EntryViewStateContext = createContext<EntryViewStateContextValue | null>(
	null,
);

function monthStart(date: Date): Date {
	return new Date(date.getFullYear(), date.getMonth(), 1);
}

function isSameDay(a: Date, b: Date): boolean {
	return (
		a.getFullYear() === b.getFullYear() &&
		a.getMonth() === b.getMonth() &&
		a.getDate() === b.getDate()
	);
}

export function EntryViewStateProvider({ children }: { children: ReactNode }) {
	const [date, setSelectedDate] = useState(() => new Date());
	const [dateRange, setSelectedDateRange] = useState<EntryViewDateRange>({
		from: undefined,
		to: undefined,
	});
	const [viewedMonth, setViewedMonthState] = useState(() =>
		monthStart(new Date()),
	);
	const [filterOverrides, setFilterOverrides] = useState<EntryFilterOverrides>(
		{},
	);

	const setDate = useCallback((nextDate: Date) => {
		setSelectedDate(nextDate);
		setSelectedDateRange({ from: undefined, to: undefined });
		setViewedMonthState(monthStart(nextDate));
	}, []);

	const setDateRange = useCallback((range: EntryViewDateRange) => {
		if (range.from && range.to && isSameDay(range.from, range.to)) {
			setSelectedDate(range.from);
			setSelectedDateRange({ from: undefined, to: undefined });
			setViewedMonthState(monthStart(range.from));
			return;
		}
		if (range.from) setSelectedDate(range.from);
		setSelectedDateRange(range);
		if (range.from) setViewedMonthState(monthStart(range.from));
	}, []);

	const setViewedMonth = useCallback((month: Date) => {
		setViewedMonthState(monthStart(month));
	}, []);

	const resetFilters = useCallback(() => setFilterOverrides({}), []);

	const value = useMemo<EntryViewStateContextValue>(
		() => ({
			date,
			dateRange,
			viewedMonth,
			filterOverrides,
			setDate,
			setDateRange,
			setViewedMonth,
			setFilterOverrides,
			resetFilters,
		}),
		[
			date,
			dateRange,
			viewedMonth,
			filterOverrides,
			setDate,
			setDateRange,
			setViewedMonth,
			resetFilters,
		],
	);

	return (
		<EntryViewStateContext.Provider value={value}>
			{children}
		</EntryViewStateContext.Provider>
	);
}

export function useEntryViewState(): EntryViewStateContextValue {
	const value = useContext(EntryViewStateContext);
	if (!value) {
		throw new Error(
			"useEntryViewState must be used within EntryViewStateProvider",
		);
	}
	return value;
}
